import React, { useCallback, useContext, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react-lite";
import { UI } from "@wwimmo/ui";
import { useParams } from "react-router";
import * as Screens from "src/screens";
import { getDateWithLongMonth } from "src/utils/Date";
import { Route, selectRoute } from "src/config/routes";
import { useTranslation } from "react-i18next";
import { ASSEMBLY_STATE } from "src/stores/assemblies/AssemblyStore";
import styles from "./LiveMode.module.css";
import { useHistory } from "react-router-dom";
import { CloseLiveMode } from "./close/CloseLiveMode";
import { ProcessPanelLiveMode } from "./processpanel/ProcessPanelLiveMode";
import { InternalCommentLiveMode } from "./internalcomment/InternalCommentLiveMode";
import { DocumentsLiveMode } from "./documents/DocumentsLiveMode";
import { Attendance } from "src/screens/assemblies/assembly/attendance/Attendance";
import { MessageType } from "src/components/notifications/Notifier";

interface Params {
    assemblyid?: string;
}

const LiveModeBase = observer(() => {
    const { agendaitemStore, assemblyStore, navStore, authStore, uiStore } = useContext(RootStoreContext);
    const { assemblyid } = useParams<Params>();
    const { t } = useTranslation();
    const history = useHistory();

    const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);

    // Update width on resize
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // If screen width is less than 768px, show a warning
    if (windowWidth < 768) {
        return (
            <div style={{ padding: "20px", textAlign: "center", fontWeight: "bold" }}>
                Your screen is too small. Please use a bigger screen.
            </div>
        );
    }

    const handleNavigateBack = useCallback(() => {
        if (agendaitemStore.currentAgendaItems.length > 0) {
            agendaitemStore.navigateBack();
        }
    }, [agendaitemStore]);

    const handleNavigateForward = useCallback(() => {
        if (agendaitemStore.currentAgendaItems.length > 0) {
            agendaitemStore.navigateForward();
        }
    }, [agendaitemStore]);

    useEffect(() => {
        if (assemblyid && assemblyStore.currentAssembly === undefined) {
            assemblyStore.loadCurrentAssembly(assemblyid);
        }
        if (!assemblyStore.assembyLiveModeHasStopped) {
            assemblyStore.startSubscriptionHasAssemblyStateChanged(assemblyid || "");
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (assemblyStore.currentAssembly) {
            navStore.setTitle(
                `${assemblyStore.currentAssembly?.title} am ${getDateWithLongMonth(
                    assemblyStore.currentAssembly?.date || ""
                )}` || ""
            );
            agendaitemStore.setCurrentAssemblyId(assemblyStore.currentAssembly.id);

            if (agendaitemStore.currentAgendaItems.length <= 0) {
                agendaitemStore.loadAgendaItems();
                assemblyStore.setIsAttendanceLiveModeTabActive(true);
            }
        }

        navStore.setActionButtonMiddle(null);
        navStore.setActionButtonRight(null);
        navStore.setBackbutton(
            selectRoute(Route.assemblies, authStore.user?.role),
            t("screens.assemblies.sidebar_title").toString()
        );
    }, [assemblyStore, authStore, navStore, t, assemblyStore.currentAssembly, assemblyid, agendaitemStore]);

    useEffect(() => {
        if (assemblyStore.assembyLiveModeHasStopped) {
            assemblyStore.setAssemblyState(5);
            uiStore.printStatusMessage(t("screens.assemblies.livemode.successfull_closed"), MessageType.INFO);
            history.push(
                selectRoute(Route.assemblyDetails, authStore.user?.role, {
                    assemblyid: assemblyStore.currentAssembly?.id || ""
                })
            );
            assemblyStore.stopSubscriptionHasAssemblyStateChanged();
            assemblyStore.setAssembyLiveModeHasStopped(false);
        }
    }, [
        assemblyStore.assembyLiveModeHasStopped,
        authStore.user?.role,
        history,
        uiStore,
        t,
        assemblyStore.currentAssembly?.id,
        assemblyStore
    ]);

    const onClickStopLiveMode = useCallback(async () => {
        await assemblyStore.updateAssemblyState(1, t("screens.assemblies.livemode.successfull_closed").toString());
        history.push(
            selectRoute(Route.assemblyDetails, authStore.user?.role, {
                assemblyid: assemblyStore.currentAssembly?.id || ""
            })
        );
        assemblyStore.setIsAttendanceLiveModeTabActive(false);
        assemblyStore.stopSubscriptionHasAssemblyStateChanged();
    }, [assemblyStore, authStore.user?.role, history, t]);

    if (!assemblyStore.currentAssembly) {
        return <Screens.Loading />;
    }

    const currentItem = agendaitemStore.currentAgendaItem;

    // Find the parent if current item is a child
    const parentItem = agendaitemStore.currentAgendaItems.find((parent) =>
        parent.agendaitemchild?.some((child) => child.id === currentItem?.id)
    );

    // Construct display text
    const agendaText =
        parentItem && currentItem
            ? `${parentItem.sort}.${currentItem.sort} ${currentItem.text}`
            : `${currentItem?.sort}. ${currentItem?.text}`;

    const renderNavigation = () => (
        <div className={styles.TitleRow}>
            <button className={styles.NavButton} onClick={handleNavigateBack}>
                <UI.Icon icon={UI.SVGIcon.ChevronLeft} size={22} />
                <div>{t("screens.assemblies.livemode.forward").toString()}</div>
            </button>
            <div className={styles.Title}>
                {assemblyStore.isCloseLiveModeTabActive
                    ? t("screens.assemblies.livemode.close")
                    : assemblyStore.isAttendanceLiveModeTabActive
                    ? t("screens.assemblies.attendanceList")
                    : agendaText}
            </div>
            <button className={styles.NavButton} onClick={handleNavigateForward}>
                <div>{t("screens.assemblies.livemode.goback").toString()}</div>
                <UI.Icon icon={UI.SVGIcon.ChevronRight} size={22} />
            </button>
        </div>
    );

    return (
        <>
            {assemblyStore.currentAssembly.state === ASSEMBLY_STATE.LIVEMODE_ACTIVE ? (
                <div className={styles.Container}>
                    <ProcessPanelLiveMode />
                    <div className={styles.MainRow}>
                        {renderNavigation()}

                        {assemblyStore.isCloseLiveModeTabActive ? (
                            <CloseLiveMode />
                        ) : assemblyStore.isAttendanceLiveModeTabActive ? (
                            <Attendance isLiveModeSmallView={windowWidth <= 1024} />
                        ) : (
                            <>
                                <UI.Card className={styles.AgendaItemCard}>
                                    <div className={styles.agendaItemText}>{agendaText}</div>
                                    <div>{agendaitemStore.currentAgendaItem?.description}</div>
                                </UI.Card>
                                <UI.Card>
                                    <div className={styles.VotingTitle}>
                                        {t("screens.assemblies.livemode.voting").toString()}
                                    </div>
                                </UI.Card>
                                <label className={styles.ProtocolLabel}>
                                    {t("screens.assemblies.livemode.protocol_comment").toString()}
                                </label>
                                <UI.Card className={styles.ProtocolTextCard}>
                                    <div>{agendaitemStore.currentAgendaItem?.protocoltext}</div>
                                </UI.Card>
                                {renderNavigation()}
                            </>
                        )}
                    </div>
                    <div className={styles.InfoRow}>
                        <UI.Card
                            className={styles.LiveModeCard}
                            title={t("screens.assemblies.livemode.title").toString()}
                        >
                            <UI.Button
                                className={styles.ButtonStopLiveMode}
                                label={t("screens.assemblies.livemode.close_button")}
                                onClick={onClickStopLiveMode}
                            />
                        </UI.Card>
                        {agendaitemStore.currentAgendaItem ? <InternalCommentLiveMode /> : undefined}
                        {agendaitemStore.currentAgendaItem ? <DocumentsLiveMode /> : undefined}
                    </div>
                </div>
            ) : undefined}
        </>
    );
});

export const LiveMode = Sentry.withProfiler(LiveModeBase);
